import PublicApiService from "../../../../../services/PublicApiService";
import {
  Admin,
  CompanyAdminApiItem,
  CompanyAdminsResponse,
  CompanySharersResponse,
  Entitlement,
  EntitlementRow,
  SharerApiItem,
  SharerCompany
} from "../../../types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import EventHelper from "../../../../../common/network/helpers/event/EventHelper";
import { Nullable } from "../../../../../common/types";
import { useSelector } from "react-redux";
import { organisationsSelector } from "../../../../../redux/organisations/selectors/organisation.selectors";

export const useExhibitorDetailsData = (participatingOrganisationId: Nullable<string>, exhibitorType?: string) => {
  const { t } = useTranslation("common");
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [isAdminsLoading, setIsAdminsLoading] = useState<Boolean>(false);
  const [entitlements, setEntitlements] = useState<EntitlementRow[]>([]);
  const [isCreateBadgeEnabled, setIsCreateBadgeEnabled] = useState<Boolean>(false);
  const [isEntitlementsLoading, setIsEntitlementsLoading] = useState<Boolean>(false);
  const [isSharersLoading, setIsSharersLoading] = useState<Boolean>(false);
  const [sharers, setSharers] = useState<SharerCompany[]>([]);
  const organisations = useSelector(organisationsSelector);
  const shouldShowAdmins = !isAdminsLoading && admins.length > 0;
  const isNetworkingEvent = EventHelper.eventEdition!.isParticipationEvent ?? false;
  const shouldShowExhibitorDetails = Boolean(participatingOrganisationId) && (isNetworkingEvent || exhibitorType !== "VisitingCompany");

  const formatAdminsInfo = (admins: CompanyAdminApiItem[] = []): Admin[] =>
    admins.map(item => ({
      name: `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim(),
      email: item.email ?? item.emailAddress ?? ""
    }));

  const formatEntitlementsToRows = (items: Entitlement[] = []): EntitlementRow[] => {
    return items
      .filter(e => Boolean(e.ecommerceProduct?.number) && e.balance.total > 0)
      .map(e => ({
        badgeProduct: e.ecommerceProduct?.number ?? "",
        entitlements: `${e.balance.remaining}/${e.balance.total} ${t("companyPage.entitlementsDialog.remaining")}`
      }));
  };

  const fetchAdmins = async () => {
    if (!participatingOrganisationId) {
      setAdmins([]);
      return;
    }
    try {
      setIsAdminsLoading(true);
      const response = (await PublicApiService.getCompanyAdmins(participatingOrganisationId)) as CompanyAdminsResponse;
      const apiAdmins = response.admins || [];

      setAdmins(formatAdminsInfo(apiAdmins));
    } catch (e) {
      console.error(e);
    } finally {
      setIsAdminsLoading(false);
    }
  };

  const fetchEntitlements = async () => {
    if (!participatingOrganisationId) {
      setEntitlements([]);
      return;
    }
    try {
      setIsEntitlementsLoading(true);
      const response = await PublicApiService.getCompanyEntitlements(participatingOrganisationId).then(data => data.data);
      const items: Entitlement[] = response.entitlements || [];
      const entitlementRows = formatEntitlementsToRows(items);
      setIsCreateBadgeEnabled(entitlementRows.some(e => e.badgeProduct));
      setEntitlements(entitlementRows);
    } catch (err) {
      console.error(err);
    } finally {
      setIsEntitlementsLoading(false);
    }
  };
  const fetchSharers = async () => {
    try {
      setIsSharersLoading(true);

      const response = (await PublicApiService.getCompanySharers(participatingOrganisationId!)) as CompanySharersResponse;

      const apiSharers = response.sharers || [];
      const mapped: SharerCompany[] = apiSharers.map((item: SharerApiItem) => {
        const multilingual = item.multilingual || [];

        const organisation = organisations.find(org => org.id === item.id);
        const orgName = organisation?.companyName;

        return {
          id: item.id,
          name: orgName ?? multilingual[0]?.displayName ?? "",
          activeBadges: item.activeBadgeCount ?? 0
        };
      });
      setSharers(mapped);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSharersLoading(false);
    }
  };

  return {
    admins,
    isAdminsLoading,
    entitlements,
    isEntitlementsLoading,
    isCreateBadgeEnabled,
    fetchAdmins,
    fetchEntitlements,
    shouldShowAdmins,
    shouldShowExhibitorDetails,
    fetchSharers,
    sharers,
    isSharersLoading
  };
};
